import { closeGenericModal } from '@innogy/common-ui-modals';
import { useValidatorIf, validateSequential } from '@innogy/utils-deprecated';
import {
  GenericModalResults,
  GenericModalSources,
} from '@legacy/common-ui-shared-interfaces';
import { createReducer, on } from '@ngrx/store';
import {
  ResetAction,
  SetUserDefinedPropertyAction,
  SetValueAction,
  addGroupControl,
  createFormGroupState,
  formStateReducer,
  markAsUnsubmitted,
  onNgrxForms,
  onNgrxFormsAction,
  reset,
  setValue,
  updateGroup,
  wrapReducerWithFormStateUpdate,
  type FormGroupState,
} from 'ngrx-forms';
import { requiredFalse } from 'ngrx-forms/validation';
import {
  ConsentStatus,
  type ConsentType,
  type GetConsentGroupResponse,
} from '@integration/api-client/consent';

import type { ConsentForm, ConsentFormValues } from '../../models';
import {
  setConsentControlValues,
  setConsentFormDataAction,
} from './consent-form.actions';

export const consentFormId = 'consent-form';

export const initialConsentFormValues: ConsentFormValues = {
  acceptAll: false,
  consents: {},
};

interface ConsentProperties {
  consentType: ConsentType;
  dependsOn?: ConsentType;
}

export const initialFormState: ConsentForm =
  createFormGroupState<ConsentFormValues>(
    consentFormId,
    initialConsentFormValues
  );

export const validateAndUpdateForms = (form: ConsentForm) => {
  return updateGroup<ConsentFormValues>(form, {
    acceptAll: setValue(checkConsentsValue(form)),
    consents: validateAndUpdateConsents,
  });
};

const checkConsentsValue = (form: ConsentForm): boolean => {
  return Object.values(form.value.consents).every(
    (consent) => consent === true
  );
};

const validateAndUpdateConsents = (
  consents: FormGroupState<{ [key: number]: boolean }>
): FormGroupState<{ [key: number]: boolean }> => {
  const consentControls = Object.keys(consents.value).reduce((acc, key) => {
    const currentControl = consents.controls[+key];
    const dependsOnControl = Object.values(consents.controls).find(
      (control) =>
        control.userDefinedProperties['consentProperties'].consentType ===
        currentControl.userDefinedProperties['consentProperties'].dependsOn
    );

    return {
      ...acc,
      [key]: validateSequential(
        useValidatorIf(requiredFalse, dependsOnControl?.value === false)
      ),
    };
  }, {});

  return updateGroup(consentControls)(consents);
};

const setControlValues = (form: ConsentForm, value: boolean) => {
  return updateGroup<ConsentFormValues>(form, {
    consents: setConsentValues(value),
  });
};

const setConsentValues =
  (value: boolean) =>
  (
    consents: FormGroupState<{ [key: number]: boolean }>
  ): FormGroupState<{ [key: number]: boolean }> => {
    const updatedConsents = Object.keys(consents.value).reduce(
      (acc, key) => ({
        ...acc,
        [key]: setValue(value),
      }),
      {}
    );

    return updateGroup(updatedConsents)(consents);
  };

const setConsentFormControls = (
  form: ConsentForm,
  consentGroup: GetConsentGroupResponse
): ConsentForm => {
  form = updateGroup<ConsentFormValues>(form, {
    consents: addConsentsControls(consentGroup),
  });

  form = formStateReducer(
    form,
    new SetUserDefinedPropertyAction(
      form.id,
      'consentGroupType',
      consentGroup.type
    )
  );

  return form;
};

const addConsentsControls =
  (consentGroup: GetConsentGroupResponse) =>
  (
    consents: FormGroupState<{ [key: number]: boolean }>
  ): FormGroupState<{ [key: number]: boolean }> => {
    consentGroup.sub_groups
      .flatMap((subGroup) => subGroup.consents)
      .filter((consent) => !consent.readonly)
      .forEach((consent) => {
        const controlDoesNotExist = () =>
          !Object.keys(consents.controls).includes(
            consent.version_id.toString()
          );
        if (controlDoesNotExist()) {
          consents = addGroupControl(
            consents,
            consent.version_id,
            consent.status === ConsentStatus.GIVEN
          );

          const consentProperties: ConsentProperties = {
            consentType: consent.consent_type,
            dependsOn: consent.depends_on,
          };

          consents = formStateReducer(
            consents,
            new SetUserDefinedPropertyAction(
              consents.controls[consent.version_id].id,
              'consentProperties',
              consentProperties
            )
          );
        }
      });

    return consents;
  };

const formReducer = createReducer<ConsentForm>(
  initialFormState,
  on(setConsentFormDataAction, (form, action) =>
    setConsentFormControls(form, action.consentGroup)
  ),
  on(setConsentControlValues, (form, action) =>
    setControlValues(form, action.payload)
  ),
  on(closeGenericModal, (form, action) => {
    if (
      action.source === GenericModalSources.CONSENT_ARE_YOU_SURE_MODAL &&
      (action.result === GenericModalResults.PRIMARY ||
        action.result === GenericModalResults.CLOSE)
    ) {
      return markAsUnsubmitted(form);
    }
    return form;
  }),
  onNgrxFormsAction(SetValueAction, (state, action) => {
    if (action.controlId === `${consentFormId}.acceptAll`) {
      return setControlValues(state, action.value as boolean);
    }
    return state;
  }),
  onNgrxFormsAction(ResetAction, (state, action) => {
    if (action.controlId === consentFormId) {
      const initialConsentFormState = setValue(state, initialConsentFormValues);

      return reset(initialConsentFormState);
    }
    return state;
  }),
  onNgrxForms()
);

export const ConsentFormReducer = wrapReducerWithFormStateUpdate(
  formReducer,
  (state) => state,
  (_, state) => validateAndUpdateForms(state)
);
